import { useState, useEffect } from 'react';
import { Calendar, Clock, MapPin, User, Plus, Building2, Stethoscope } from 'lucide-react';
import toast from 'react-hot-toast';
import { getCirugiasPendientes, Cirugia, getTipoCirugia, getHospital } from '../../services/cirugiasService';
import medicoVfpService, { PacienteVfp } from '../../services/medicoVfpService';
import DetalleCirugiaModal from './DetalleCirugiaModal';
import FormCirugiaModal from './FormCirugiaModal';
// Building2 y Stethoscope se usan en el JSX
// getTipoCirugia y getHospital se usan en loadCirugiasPendientes
// medicoVfpService se usa en loadCirugiasPendientes

export default function CirugiasPendientes() {
  const [cirugias, setCirugias] = useState<Cirugia[]>([]);
  const [selectedCirugia, setSelectedCirugia] = useState<Cirugia | null>(null);
  const [showDetalleModal, setShowDetalleModal] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pacientesMap, setPacientesMap] = useState<Record<number, PacienteVfp>>({});
  const [tiposCirugiaMap, setTiposCirugiaMap] = useState<Record<number, any>>({});
  const [hospitalesMap, setHospitalesMap] = useState<Record<number, any>>({});

  useEffect(() => {
    loadCirugiasPendientes();
  }, []);

  const loadCirugiasPendientes = async () => {
    try {
      setLoading(true);
      const response = await getCirugiasPendientes();
      const data = Array.isArray(response) ? response : (response.data || []);
      setCirugias(data);

      // Cargar datos relacionados
      const newPacientesMap: Record<number, PacienteVfp> = { ...pacientesMap };
      const newTiposCirugiaMap: Record<number, any> = { ...tiposCirugiaMap };
      const newHospitalesMap: Record<number, any> = { ...hospitalesMap };

      for (const cirugia of data) {
        // Cargar paciente si no está en caché
        if (cirugia.pacienteId && !newPacientesMap[cirugia.pacienteId]) {
          try {
            const paciente = await medicoVfpService.getPacienteVfp(cirugia.pacienteId);
            newPacientesMap[cirugia.pacienteId] = paciente;
          } catch (error) {
            console.error(`Error al cargar paciente ${cirugia.pacienteId}:`, error);
          }
        }

        // Cargar tipo de cirugía si no está en caché
        if (cirugia.tipoCirugiaId && !newTiposCirugiaMap[cirugia.tipoCirugiaId]) {
          try {
            const tipo = await getTipoCirugia(cirugia.tipoCirugiaId);
            newTiposCirugiaMap[cirugia.tipoCirugiaId] = tipo;
          } catch (error) {
            console.error(`Error al cargar tipo de cirugía ${cirugia.tipoCirugiaId}:`, error);
          }
        }

        // Cargar hospital si no está en caché
        if (cirugia.hospitalId && !newHospitalesMap[cirugia.hospitalId]) {
          try {
            const hospital = await getHospital(cirugia.hospitalId);
            newHospitalesMap[cirugia.hospitalId] = hospital;
          } catch (error) {
            console.error(`Error al cargar hospital ${cirugia.hospitalId}:`, error);
          }
        }
      }

      setPacientesMap(newPacientesMap);
      setTiposCirugiaMap(newTiposCirugiaMap);
      setHospitalesMap(newHospitalesMap);
    } catch (error) {
      console.error('Error al cargar cirugías pendientes:', error);
      toast.error('Error al cargar cirugías pendientes');
    } finally {
      setLoading(false);
    }
  };

  const handleCirugiaClick = (cirugia: Cirugia) => {
    setSelectedCirugia(cirugia);
    setShowDetalleModal(true);
  };

  const getEstadoBadgeColor = (estado: string) => {
    switch (estado) {
      case 'prospecto':
        return 'bg-yellow-100 text-yellow-800';
      case 'confirmada':
        return 'bg-green-100 text-green-800';
      case 'en_proceso':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatFecha = (fecha: string) => {
    const [year, month, day] = fecha.split('T')[0].split('-').map(Number);
    const parts = new Intl.DateTimeFormat('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).formatToParts(new Date(year, month - 1, day));
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
    const mon = get('month').replace('.', '').toUpperCase();
    return `${get('day')} ${mon} ${get('year')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Cirugías Pendientes</h2>
          <p className="text-sm text-gray-600 mt-1">
            {cirugias.length} cirugía{cirugias.length !== 1 ? 's' : ''} pendiente{cirugias.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={() => setShowFormModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Nueva Cirugía
        </button>
      </div>

      {/* Lista de Cirugías */}
      {cirugias.length === 0 ? (
        <div className="text-center py-12">
          <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No hay cirugías pendientes</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {cirugias.map((cirugia) => (
            <div
              key={cirugia.id}
              onClick={() => handleCirugiaClick(cirugia)}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    {cirugia.padecimiento}
                  </h3>
                  <div className="flex items-center gap-4 text-sm text-gray-600 flex-wrap">
                    <div className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      {pacientesMap[cirugia.pacienteId]
                        ? `${pacientesMap[cirugia.pacienteId].nombre || ''} ${pacientesMap[cirugia.pacienteId].apellido1 || ''}`.trim()
                        : 'Paciente'}
                    </div>
                    <div className="flex items-center gap-1">
                      <Stethoscope className="w-4 h-4" />
                      {tiposCirugiaMap[cirugia.tipoCirugiaId]?.nombre || 'Tipo de cirugía'}
                    </div>
                  </div>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-medium ${getEstadoBadgeColor(
                    cirugia.estado
                  )}`}
                >
                  {cirugia.estado}
                </span>
              </div>

              <div className="grid grid-cols-1 gap-3 text-sm mb-3">
                <div className="flex items-center gap-2 text-gray-600">
                  <Building2 className="w-4 h-4" />
                  <span>{hospitalesMap[cirugia.hospitalId]?.nombre || 'Hospital'}</span>
                </div>
                <div className="flex items-center gap-4 text-gray-600 flex-wrap">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {formatFecha(cirugia.fechaProgramada)}
                  </div>
                  {cirugia.horaInicio && (
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {cirugia.horaInicio}
                    </div>
                  )}
                  {cirugia.salaQuirurgica && (
                    <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      Sala: {cirugia.salaQuirurgica}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-600 pb-3 border-b border-gray-200">
                <User className="w-4 h-4" />
                <span>
                  {cirugia.tipoPago === 'seguro' ? 'Con Seguro' : 'Sin Seguro'}
                </span>
              </div>

              {cirugia.observaciones && (
                <div className="mt-3 pt-3">
                  <p className="text-sm text-gray-600 line-clamp-2">
                    {cirugia.observaciones}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modales */}
      {showDetalleModal && selectedCirugia && (
        <DetalleCirugiaModal
          cirugia={selectedCirugia}
          onClose={() => {
            setShowDetalleModal(false);
            setSelectedCirugia(null);
          }}
          onUpdate={loadCirugiasPendientes}
        />
      )}

      {showFormModal && (
        <FormCirugiaModal
          onClose={() => setShowFormModal(false)}
          onSuccess={() => {
            setShowFormModal(false);
            loadCirugiasPendientes();
          }}
        />
      )}
    </div>
  );
}
